import type { LucideIcon } from 'lucide-react';
import { ResourceType } from '@cotera/api';
import { cn } from '@cotera/client/v0/lib/utils';
import { resourceTypeIconRingClassName } from './resource-type-colors';

/**
 * Renders a resource's icon — or its custom emoji — inside a color-coded tile
 * whose color signals the resource type. The tile keeps the same `h-5 w-5`
 * footprint as a bare list-row icon, so it drops into the existing icon column
 * without any layout changes.
 *
 * Rounded-rect, not a circle: circles are how this UI draws *people*
 * (`MemberAvatar`, `ScopeIconCircle`), so a circular resource icon made the
 * shape ambiguous. `rounded-md` also nests correctly — tighter than the card
 * around it (`rounded-lg`), in the same family as the action buttons
 * (`rounded-sm`).
 *
 * Retaining the tile for emoji agents is deliberate: the color (not the glyph)
 * is what tells the user "this is an agent", so the association survives a custom
 * emoji.
 */
export function ResourceTypeIconBubble({
  resourceType,
  icon: Icon,
  emoji,
  className,
  iconClassName = 'h-5 w-5',
}: {
  resourceType: ResourceType;
  /** Fallback icon shown when there is no custom emoji. */
  icon: LucideIcon;
  /** Custom emoji, if the resource has one. */
  emoji?: string | null;
  className?: string;
  /**
   * Glyph size, for callers that shrink the tile. Defaults to the list-row size;
   * a menu row wants a smaller tile, and the glyph has to come down with it or it
   * fills the whole bubble edge to edge.
   */
  iconClassName?: string;
}) {
  const hasEmoji = emoji !== undefined && emoji !== null && emoji !== '';
  return (
    <span
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
        resourceTypeIconRingClassName(resourceType),
        className
      )}
    >
      {hasEmoji ? (
        <span className="text-base leading-none">{emoji}</span>
      ) : (
        // Lucide icons stroke with `currentColor`, so they inherit the bubble's
        // text color from `resourceTypeIconRingClassName`. Icon size matches the
        // pre-bubble list icons (h-5 w-5); the bubble grows around it.
        <Icon className={iconClassName} strokeWidth={1.75} />
      )}
    </span>
  );
}
